import React, { Dispatch, SetStateAction } from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as z from 'zod';

import { commonMessages } from 'src/features/auth/utils';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { LIST_MEMBERSHIPS_RESOURCE } from 'src/features/userManagement/api';
import { MEMBERSHIP_VALIDATOR_RESOURCE } from 'src/features/userManagement/api/useRetrieveMembershipValidator';
import { useUpdateMembership } from 'src/features/userManagement/api/useUpdateMembership';
import CommonFormFields from 'src/features/userManagement/components/forms/CommonFormFields';
import { ALLOWED_VALIDATION, type Membership, MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { userManagementMessages } from 'src/features/userManagement/utils';
import { useMembershipValidatorUpdate } from 'src/features/validation/hooks/useMembershipValidatorUpdate';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps } from 'src/registry/components/table';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import {
    Form,
    FormControl,
    FormControlStack,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { type GeneralSmartParkError } from 'src/types';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';
import type { ValidatorSettings } from 'src/features/validation/types';

interface EditUserModalFormProps extends DataTableNewEntryProps<Membership> {
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    membershipData?: Membership | null;
    validatorSettings?: ValidatorSettings;
}

export type EditUserFormSchemaValues = z.infer<typeof formSchema>;
const formSchema = z
    .object({
        first_name: z.string().min(1),
        last_name: z.string().min(1),
        role: z.nativeEnum(MEMBERSHIP_ROLES),
        allowed_validation: z
            .union([z.nativeEnum(ALLOWED_VALIDATION), z.literal(null)])
            .optional()
            .default(null),
        allowed_pricing_plan: z
            .union([z.string(), z.literal(null)])
            .optional()
            .default(null),
        guest_reasons: z.array(z.string()),
    })
    .superRefine((data, ctx) => {
        if (data.role === MEMBERSHIP_ROLES.VALIDATOR) {
            if (!data.allowed_validation) {
                ctx.addIssue({
                    path: ['allowed_validation'],
                    code: z.ZodIssueCode.custom,
                });
            }
            if (data.allowed_validation === ALLOWED_VALIDATION.PRICING_PLAN && !data.allowed_pricing_plan) {
                ctx.addIssue({
                    path: ['allowed_pricing_plan'],
                    code: z.ZodIssueCode.custom,
                });
            }
        }
    });

function EditUserModalForm({ setIsModalOpen, membershipData, validatorSettings }: EditUserModalFormProps) {
    const { formatMessage } = useIntl();
    const { toast } = useToast();
    const { parkingId } = useParking();

    const isReady =
        !!membershipData?.user?.first_name &&
        !!membershipData?.user?.last_name &&
        !!membershipData?.role &&
        validatorSettings !== undefined;

    const defaultValues = React.useMemo(() => {
        if (!isReady) return {};
        return {
            role: membershipData?.role,
            first_name: membershipData?.user.first_name,
            last_name: membershipData?.user?.last_name,
            allowed_validation: validatorSettings?.allowed_validation ?? null,
            allowed_pricing_plan: validatorSettings?.allowed_pricing_plan ?? null,
            guest_reasons: validatorSettings?.guest_reasons ?? [],
        };
    }, [validatorSettings, membershipData, isReady]);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues,
        values: defaultValues,
        resetOptions: {
            keepDirtyValues: false,
        },
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, defaultValues]);

    const {
        role: selectedRole,
        allowed_validation: selectedValidationType,
        allowed_pricing_plan: selectedValidationPricingPlan,
        guest_reasons
    } = form.watch()

    React.useEffect(() => {
        if (selectedRole !== MEMBERSHIP_ROLES.VALIDATOR && selectedRole !== undefined) {
            form.setValue('allowed_validation', null);
            form.setValue('allowed_pricing_plan', null);
            void form.trigger();
        }
    }, [selectedRole]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset(defaultValues);
        }
        if (setIsModalOpen) {
            setIsModalOpen(isOpen);
        }
    };

    const onErrorHandle = (error: GeneralSmartParkError) => {
        const errorData = error.response?.data;
        const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
        const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

        toast({
            title: errorCode,
            description: errorMessage,
            variant: 'destructive',
        });
    };

    const { mutate: updateMembershipRole, isPending: isPendingUpdate } = useUpdateMembership({
        onSuccess: () => {
            onOpenChange(false);
            toast({
                title: formatMessage(membershipMessages.updateMembershipGroupSuccess),
                description: formatMessage(membershipMessages.updateMembershipRoleSuccessDescription),
            });
            void queryClient.invalidateQueries({
                queryKey: [LIST_MEMBERSHIPS_RESOURCE, { parkingId }],
            });
        },
        onError: (error) => onErrorHandle(error),
    });

    const { mutate: updateMembershipValidator } = useMembershipValidatorUpdate({
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [MEMBERSHIP_VALIDATOR_RESOURCE, { parkingId, membershipId: membershipData?.id }],
            });
        },
        onError: (error) => onErrorHandle(error),
    });

    const onSubmit = ({ ...values }: EditUserFormSchemaValues) => {
        const submitData = {
            parkingId: parkingId,
            role: values.role,
            user_first_name: values.first_name,
            user_last_name: values.last_name,
            guest_reasons: values.guest_reasons,
        };
        const submitDataValidatorUpdate = {
            parkingId: parkingId,
            allowed_validation: selectedRole !== MEMBERSHIP_ROLES.VALIDATOR ? null : values.allowed_validation,
            allowed_pricing_plan:
                selectedValidationType === ALLOWED_VALIDATION.GUEST ? null : values.allowed_pricing_plan,
            guest_reasons: values.guest_reasons,
        };
        if (membershipData?.id) {
            updateMembershipRole({ membershipId: membershipData?.id, ...submitData });
            if (selectedRole) {
                updateMembershipValidator({ membershipId: membershipData?.id, ...submitDataValidatorUpdate });
            }
        }
    };

    const disableButtonHandler = (): boolean => {
        if (!form.formState.isValid) return true;
        if (!defaultValues) return true;
        const isValidator = selectedRole === MEMBERSHIP_ROLES.VALIDATOR;

        const isNameChanged =
            form.getValues('first_name') !== defaultValues.first_name ||
            form.getValues('last_name') !== defaultValues.last_name;
        const isRoleChanged = selectedRole !== defaultValues.role;
        const isValidationTypeChanged = selectedValidationType !== defaultValues.allowed_validation;
        const isPricingPlanChanged = selectedValidationPricingPlan !== defaultValues.allowed_pricing_plan;
        const hasGuestReasonsChanged = guest_reasons !== defaultValues.guest_reasons;

        if (!isValidator) {
            return !(isNameChanged || isRoleChanged); // disable if no real change
        }

        if (isValidator) {
            if (!selectedValidationType) return true;

            if (selectedValidationType === ALLOWED_VALIDATION.GUEST) {
                return !(isNameChanged || isRoleChanged || isValidationTypeChanged || hasGuestReasonsChanged);
            }

            if (selectedValidationType === ALLOWED_VALIDATION.PRICING_PLAN) {
                if (!selectedValidationPricingPlan) return true;

                return !(isNameChanged || isRoleChanged || isValidationTypeChanged || isPricingPlanChanged);
            }
        }

        return !form.formState.isDirty;
    };

    const editTitle = formatMessage(userManagementMessages.editUser);
    if (!isReady || !defaultValues) return null;

    return (
        <Dialog modal open={true} onOpenChange={setIsModalOpen}>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={faEdit} />
                        <div>
                            <DialogTitle>{editTitle}</DialogTitle>
                            <DialogDescription>{`${editTitle} ${membershipData.user.email}`}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
                        <FormField
                            control={form.control}
                            name="first_name"
                            render={({ field }) => (
                                <FormItem variant="dialog" className="w-full">
                                    <FormLabel variant="dialog" required>
                                        {formatMessage(commonMessages.firstName)}
                                    </FormLabel>
                                    <FormControlStack className="mr-3">
                                        <FormControl>
                                            <Input type="text" {...field} value={field.value ?? ''} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="last_name"
                            render={({ field }) => (
                                <FormItem variant="dialog" className="w-full">
                                    <FormLabel variant="dialog" required>
                                        {formatMessage(commonMessages.lastName)}
                                    </FormLabel>
                                    <FormControlStack className="mr-3">
                                        <FormControl>
                                            <Input type="text" {...field} value={field.value ?? ''} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                        {defaultValues.role !== MEMBERSHIP_ROLES.CLIENT && (
                            <CommonFormFields<EditUserFormSchemaValues>
                                control={form.control}
                                fieldNames={{
                                    role: 'role',
                                    validator_type: 'allowed_validation',
                                    validator_pricing_plan: 'allowed_pricing_plan',
                                }}
                            />
                        )}
                    </Form>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => {
                                setIsModalOpen(false);
                            }}
                        >
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        disabled={disableButtonHandler()}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPendingUpdate ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        <FormattedMessage {...userManagementMessages.editUser} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { EditUserModalForm };
